<template>
  <DefineDetailSectionTemplate #default="{ details }">
    <BaseCard>
      <h3
        v-if="details.label"
        class="mb-3 text-sm font-semibold text-background-fg/70"
      >
        {{ details.label }}
      </h3>
      <dl class="space-y-2">
        <template
          v-for="detail in details.details"
          :key="detail.label"
        >
          <div
            :class="[
              detail.value === true
                ? 'flex justify-between bg-positive/8'
                : '',
              'rounded px-2 py-1',
            ]"
          >
            <dt class="text-xs text-background-fg/50">
              {{ detail.label }}
            </dt>
            <dd
              class="shrink-0 overflow-x-auto no-scrollbar"
            >
              <template v-if="detail.value === true">
                <div
                  class="flex size-4 items-center justify-center rounded"
                >
                  <IconCheck class="text-positive" />
                </div>
              </template>
              <template v-else-if="detail.value === false">
              </template>
              <template
                v-else-if="
                  detail.value !== undefined &&
                  detail.value !== null
                "
              >
                <span class="text-sm font-medium">
                  {{ detail.value }}
                </span>
              </template>
              <template v-else> - </template>
            </dd>
          </div>
        </template>
      </dl>
    </BaseCard>
  </DefineDetailSectionTemplate>

  <template v-if="isFetching">
    <div class="size-32">
      <BaseInnerLoading spinner-class="size-8" />
    </div>
  </template>
  <template v-else-if="data">
    <div class="flex items-center justify-between pt-4">
      <h2
        :class="[
          'max-w-[668px] truncate pr-5 text-lg font-semibold',
          data.isActive
            ? 'text-background-fg'
            : 'text-background-fg/60',
        ]"
      >
        {{ data.businessName }}
        <span v-if="!data.isActive">(disabled)</span>
      </h2>
      <div class="shrink-0">
        <RouterLink
          v-if="permissions.includes('create_merchant')"
          :to="{
            name: 'merchantEdit',
            params: {
              merchantId: parseInt(tenantId),
            },
          }"
          class="whitespace-nowrap text-primary"
        >
          Edit Merchant
        </RouterLink>
      </div>
    </div>
    <div class="w-full max-w-3xl py-4">
      <div class="mb-4">
        <div class="border-b border-gray-200">
          <nav
            class="-mb-px flex space-x-8"
            aria-label="Tabs"
          >
            <button
              v-for="tab in TABS"
              :key="tab"
              :class="[
                currentTab === tab
                  ? 'border-primary text-primary'
                  : 'border-transparent text-background-fg/70 hover:border-background-fg/8 hover:text-background-fg/87',
                'line-clamp-1 whitespace-nowrap border-b-2 px-1 py-2 text-sm font-medium',
              ]"
              @click="goToTab(tab)"
              >{{ tab }}</button
            >
          </nav>
        </div>
      </div>
      <template v-if="currentTab === 'Details'">
        <div class="grid grid-cols-2 gap-4 sm:grid-cols-2">
          <div class="flex flex-col space-y-4">
            <DetailSectionTemplate
              :details="businessDetails"
            />
          </div>
          <div class="flex flex-col space-y-4">
            <DetailSectionTemplate
              :details="customerDetails"
            />
          </div>
        </div>
      </template>
      <template v-else-if="currentTab === 'Config'">
        <div class="grid grid-cols-2 gap-4 sm:grid-cols-2">
          <div class="flex flex-col space-y-4">
            <DetailSectionTemplate
              :details="modulesConfigDetails"
            />
            <DetailSectionTemplate
              :details="advancedDetails"
            />
          </div>
          <div class="flex flex-col space-y-4">
            <DetailSectionTemplate
              :details="cardstreamConfigDetails"
            />
            <DetailSectionTemplate
              :details="acquirerConfigDetails"
            />
          </div>
        </div>
      </template>
      <template
        v-else-if="currentTab === 'Takepayments+ Features'"
      >
        <div class="grid grid-cols-2 gap-4 sm:grid-cols-2">
          <div class="flex flex-col space-y-4">
            <DetailSectionTemplate
              :details="paymentFeaturesDetails"
            />
          </div>
          <div class="flex flex-col space-y-4">
            <DetailSectionTemplate
              :details="salesFeaturesDetails"
            />
            <DetailSectionTemplate
              :details="vatFeaturesDetails"
            />
          </div>
        </div>
      </template>
      <template v-else-if="currentTab === 'Statistics'">
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
          <DetailSectionTemplate :details="statsDetails" />
          <dl>
            <dt
              class="mb-2 text-sm font-semibold text-background-fg/87"
            >
              Busiest Days (All Time)
            </dt>
            <dd>
              <ChartHeatmap
                :data="dayHeatmapData"
                :labels="dayHeatmapLabels"
                class="w-[21.5rem]"
              />
            </dd>
            <dt
              class="mb-2 text-sm font-semibold text-background-fg/87"
            >
              Busiest Days (Last Week)
            </dt>
            <dd>
              <ChartHeatmap
                :data="dayHeatmapDataLastWeek"
                :labels="dayHeatmapLabels"
                class="w-[21.5rem]"
              />
            </dd>
          </dl>
        </div>
      </template>

      <template v-else-if="currentTab === 'Engagement'">
        <MerchantDetailEngagement :tenant-id="tenantId" />
      </template>

      <template v-else-if="currentTab === 'Terminals'">
        <MerchantDetailTerminals :tenant-id="tenantId" />
      </template>
    </div>
  </template>
</template>

<script setup lang="ts">
type DetailSection = {
  label: string
  details: { label: string; value: unknown }[]
}

const props = defineProps<{
  tenantId: string
}>()

const [DefineDetailSectionTemplate, DetailSectionTemplate] =
  createReusableTemplate<{ details: DetailSection }>()

const { data, isFetching } = useMerchant(
  computed(() => parseInt(props.tenantId))
)
const { defaultMerchantCurrency } = useOrganizationConfig()

const { permissions } = useAuth()

const TABS = [
  "Details",
  "Config",
  "Takepayments+ Features",
  "Statistics",
  "Engagement",
  "Terminals",
]

type Tab = (typeof TABS)[number]
const currentTab = ref<Tab>("Details")

onMounted(() => {
  if (route.query.initialTab) {
    currentTab.value = route.query.initialTab.toString()
  }
})

const route = useRoute()

const goToTab = (tab: Tab) => {
  currentTab.value = tab
}

const businessDetails = computed<DetailSection>(() => ({
  label: "Business",
  details: [
    {
      label: "Reference",
      value: data.value?.tpReference,
    },
    {
      label: "Business Name",
      value: data.value?.businessName,
    },
    {
      label: "Merchant Category Code (MCC)",
      value: data.value?.merchantCategoryCode,
    },
    {
      label: "Currency",
      value: data.value?.currency,
    },
    {
      label: "Address Line 1",
      value: data.value?.addressLine1,
    },
    {
      label: "Address Line 2",
      value: data.value?.addressLine2,
    },
  ],
}))

const customerDetails = computed<DetailSection>(() => ({
  label: "Customer",
  details: [
    {
      label: "Customer Name",
      value: data.value?.customerName,
    },
    {
      label: "Customer Email",
      value: data.value?.customerEmail,
    },
    {
      label: "Customer Phone",
      value: data.value?.customerPhone,
    },
  ],
}))

const advancedDetails = computed<DetailSection>(() => ({
  label: "Advanced",
  details: [
    {
      label: "Is this a demo merchant?",
      value: data.value?.isDemoMerchant,
    },
    {
      label: "Is this merchant from the backbook?",
      value: data.value?.isFromBackbook,
    },
  ],
}))

const modulesConfigDetails = computed<DetailSection>(
  () => ({
    label: "Modules",
    details: [
      {
        label: "Beepaid GO",
        value: data.value?.hasBeepaidGoModule,
      },
      {
        label: "Order & Beepaid",
        value: data.value?.hasBeepaidOrderModule,
      },
      {
        label: "Takepayments+ Sales Module",
        value: data.value?.hasSalesModule,
      },
      {
        label: "Takepayments+ Donations Module",
        value: data.value?.hasDonationsModule,
      },
    ],
  })
)

const cardstreamConfigDetails = computed<DetailSection>(
  () => ({
    label: "Cardstream",
    details: [
      {
        label: "Cardstream Merchant ID",
        value: data.value?.cardstreamMerchantId,
      },
      {
        label: "Cardstream Merchant Secret",
        value: data.value?.cardstreamMerchantSecret,
      },
    ],
  })
)

const acquirerConfigDetails = computed<DetailSection>(
  () => ({
    label: "Acquirer",
    details: [
      {
        label: "Acquirer",
        value:
          data.value?.acquirer === "barclaycard-elavon"
            ? "Barclaycard/Elavon"
            : data.value?.acquirer,
      },
      {
        label:
          "Acquirer Merchant ID (A920) or NAV Site Reference (DX8000)",
        value: data.value?.paxMerchantId,
      },
      ...(data.value?.acquirer === "evo"
        ? [
            {
              label: "Gratuity is available",
              value:
                data.value?.evoConfig.gratuityIsAvailable,
            },
          ]
        : []),
    ],
  })
)

const paymentFeaturesDetails = computed<DetailSection>(
  () => ({
    label: "Payments",
    details: [
      {
        label: "Are cash payments enabled?",
        value: data.value?.isCashEnabled,
      },
      {
        label: "Are split payments enabled?",
        value: data.value?.isSplitPaymentsEnabled,
      },
      {
        label: "Is grautuity enabled?",
        value: data.value?.isGratuityEnabled,
      },
      {
        label: "Are unlinked refunds enabled?",
        value: data.value?.isUnlinkedRefundsEnabled,
      },
      {
        label: "Is service charge enabled?",
        value: data.value?.isServiceChargeEnabled,
      },
    ],
  })
)

const vatFeaturesDetails = computed<DetailSection>(() => ({
  label: "VAT",
  details: [
    {
      label: "Is VAT enabled?",
      value: data.value?.isVatEnabled,
    },
    {
      label: "VAT Number",
      value: data.value?.vatNumber,
    },
  ],
}))

const salesFeaturesDetails = computed<DetailSection>(
  () => ({
    label: "Sales",
    details: [
      {
        label: "Are products enabled?",
        value: data.value?.isProductsEnabled,
      },
      {
        label: "Are cashiers enabled?",
        value: data.value?.isCashiersEnabled,
      },
    ],
  })
)

const dayHeatmapData = computed(
  () =>
    data.value?.transactionCountsByWeekday.map(
      ({ dayOfWeek, transactionCount }) => ({
        x: ISO_DAYS[dayOfWeek],
        y: transactionCount,
      })
    ) || []
)

const dayHeatmapDataLastWeek = computed(
  () =>
    data.value?.transactionCountsByWeekdayLastWeek.map(
      ({ dayOfWeek, transactionCount }) => ({
        x: ISO_DAYS[dayOfWeek],
        y: transactionCount,
      })
    ) || []
)

const statsDetails = computed(() => ({
  label: "",
  details: [
    {
      label: "Merchant Created",
      value: data.value?.createdAt
        ? `${formatToDate(data.value?.createdAt, "EEE do MMM yyyy")}`
        : "-",
    },
    {
      label: "First Transacted At",
      value: data.value?.firstTransactedAt
        ? `${formatToDate(data.value?.firstTransactedAt, "EEE do MMM yyyy")}`
        : "-",
    },
    {
      label: "Last Transacted At",
      value: data.value?.lastTransactedAt
        ? `${formatToDate(data.value?.lastTransactedAt, "EEE do MMM yyyy")}`
        : "-",
    },
    {
      label: "Transacted Total",
      value: formatToCurrencyString(
        data.value?.transactionTotal || 0,
        data.value?.currency ??
          defaultMerchantCurrency.value
      ),
    },
    {
      label: "Number of Transactions",
      value: data.value?.transactionCount,
    },
    {
      label: "Average Transaction",
      value: formatToCurrencyString(
        (data.value?.transactionTotal || 0) /
          (data.value?.transactionCount || 1),
        data.value?.currency ??
          defaultMerchantCurrency.value
      ),
    },
    {
      label: `Merchant statistics refreshed at ${
        data.value?.merchantStatsRefreshedAt &&
        DateFns.format(
          data.value.merchantStatsRefreshedAt,
          "HH:mm dd/MM/yyyy"
        )
      }`,
      value: false,
    },
  ],
}))

const ISO_DAYS = {
  1: "Mo",
  2: "Tu",
  3: "We",
  4: "Th",
  5: "Fr",
  6: "Sa",
  7: "Su",
}
const dayHeatmapLabels = Object.values(ISO_DAYS)
</script>
